import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import {
  useGetPettyCashCategoriesQuery,
  useCreatePettyCashCategoryMutation,
  useUpdatePettyCashCategoryMutation,
} from "../../pettyCashApi";
import { useCanManageCategories } from "../../usePettyCashPermissions";

// Flat, global reference table (no branch scope, no workflow) - kept as a
// simple list + inline form, same pattern as AreasPage.jsx, rather than the
// split-view used for Fund/Voucher/Refill/Reconciliation which actually have
// per-record detail worth a whole pane.
const CategoriesPage = () => {
  const canManage = useCanManageCategories();
  const { data: categories = [], isLoading } = useGetPettyCashCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] = useCreatePettyCashCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdatePettyCashCategoryMutation();

  const [mode, setMode] = useState("new"); // "new" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [receiptRequired, setReceiptRequired] = useState(false);

  const selected = categories.find((c) => c.categoryId === selectedId) || null;

  useEffect(() => {
    if (mode === "new") {
      setName("");
      setIsActive(true);
      setReceiptRequired(false);
    } else if (selected) {
      setName(selected.categoryName);
      setIsActive(selected.isActive);
      setReceiptRequired(!!selected.receiptRequired);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selected]);

  const startNew = () => {
    setReceiptRequired(false);
    setSelectedId(null);
    setMode("new");
  };
  const startEdit = (categoryId) => {
    setSelectedId(categoryId);
    setMode("edit");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      if (mode === "edit" && selected) {
        await updateCategory({ categoryId: selected.categoryId, categoryName: name.trim(), isActive, receiptRequired }).unwrap();
      } else {
        await createCategory({ categoryName: name.trim(), receiptRequired }).unwrap();
      }
      toast.success("Category saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save category");
    }
  };

  const columns = [
    { title: "Category", dataIndex: "categoryName" },
    {
      title: "Receipt",
      dataIndex: "receiptRequired",
      render: (v) =>
        v ? (
          <span className="badge badge-linewarning">Receipt required</span>
        ) : (
          <span className="text-muted">&mdash;</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v) => <span className={`badge ${v ? "badge-linesuccess" : "badge-linedanger"}`}>{v ? "Active" : "Inactive"}</span>,
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => startEdit(record.categoryId)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Petty Cash Categories</h4>
              <h6>Expense categories chosen when a voucher is paid</h6>
            </div>
          </div>
        </div>

        {!canManage && (
          <div className="alert alert-info">You have read-only access. Managing categories requires PettyCash.ManageCategories.</div>
        )}

        {canManage && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">{mode === "edit" ? "Edit Category" : "New Category"}</h5>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Name</label>
                  <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="col-md-4 mb-3 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="category-receipt-required"
                      checked={receiptRequired}
                      onChange={(e) => setReceiptRequired(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="category-receipt-required">
                      Receipt required
                    </label>
                  </div>
                </div>
                {mode === "edit" && (
                  <div className="col-md-4 mb-3 d-flex align-items-end">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="category-active"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="category-active">Active</label>
                    </div>
                  </div>
                )}
              </div>
              <small className="text-muted d-block mb-3">
                A voucher paid under a receipt-required category shows as <strong>Paid &middot; Receipt pending</strong>
                until a receipt is attached. It never blocks the payment, and a receipt can be attached at any time.
              </small>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                {mode === "edit" && (
                  <button type="button" className="btn btn-secondary" onClick={startNew}>Clear</button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={categories} rowKey={(c) => c.categoryId} loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
